/**
 * firebase.js — SDK initialisation.
 *
 * On the Firebase web config being "public": it is, and that is fine — it
 * identifies your project, it is not a credential. What actually protects you
 * is (a) Security Rules, (b) App Check, and (c) an API-key referrer
 * restriction set in the Google Cloud console. Do all three. See SECURITY.md.
 *
 * The values still come from env vars so that staging and production cannot be
 * mixed up by a stray commit, and so a fork of the repo does not inherit your
 * project.
 */

import { initializeApp } from 'firebase/app';
import {
  initializeAuth,
  browserLocalPersistence,
  browserSessionPersistence,
  indexedDBLocalPersistence,
  GoogleAuthProvider,
} from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

const required = (name) => {
  const value = import.meta.env[name];
  if (!value) {
    // Fail loudly at boot rather than with a cryptic Firebase error six screens
    // later. A misconfigured deploy should be obvious in five seconds.
    throw new Error(
      `Missing ${name}. Copy .env.example to .env.local and fill in your Firebase config.`
    );
  }
  return value;
};

const firebaseConfig = {
  apiKey: required('VITE_FIREBASE_API_KEY'),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: required('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: required('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: required('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: required('VITE_FIREBASE_APP_ID'),
};

export const app = initializeApp(firebaseConfig);

/* ── App Check ──────────────────────────────────────────────────────────────
   This is the control that stops someone extracting your config from the JS
   bundle and hammering Firestore or the AI endpoint from a script. Without it,
   Security Rules still hold, but nothing stops an attacker burning your quota
   and your OpenAI budget with valid-looking traffic.

   In development, set VITE_APPCHECK_DEBUG_TOKEN and register the printed token
   in the Firebase console under App Check > Apps > Manage debug tokens.
   ────────────────────────────────────────────────────────────────────────── */
if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
  // eslint-disable-next-line no-undef
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
}

if (import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(import.meta.env.VITE_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
} else if (import.meta.env.PROD) {
  /**
   * This used to `throw`, which meant the first real deploy of clincordant.com
   * would have been a white screen: the module fails at evaluation, React never
   * mounts, and there is nothing on the page to explain why.
   *
   * The intent was right — shipping without App Check is a billing incident
   * waiting to happen, because anyone can lift the config out of the JS bundle
   * and run the AI endpoint on your OpenAI budget. But refusing to boot is a
   * bad way to enforce it: it blocks the site for real users while doing
   * nothing to the attacker, who was never going to load the page.
   *
   * So it warns, loudly, and the server-side guard is where enforcement
   * belongs — set ENFORCE_APPCHECK=true in functions/.env once
   * docs/APP-CHECK-SETUP.md is done. Until then the site is live and Security
   * Rules are still doing their job; what is missing is quota protection.
   */
  // eslint-disable-next-line no-console
  console.warn(
    '[Clincordant] App Check is not configured. Security Rules still apply, but ' +
    'nothing is protecting your Firebase quota or OpenAI spend from scripted ' +
    'abuse. Set VITE_RECAPTCHA_SITE_KEY — see docs/APP-CHECK-SETUP.md.'
  );
}

/* ── Auth ───────────────────────────────────────────────────────────────────
   IndexedDB first (survives restarts), falling back through localStorage to
   session-only. Safari private mode and locked-down NHS/hospital browsers
   reject IndexedDB, and a clinician on a ward PC must still be able to log in.
   ────────────────────────────────────────────────────────────────────────── */
export const auth = initializeAuth(app, {
  persistence: [
    indexedDBLocalPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
  ],
});

export const googleProvider = new GoogleAuthProvider();
// Always show the account chooser: on a shared clinical workstation, silently
// reusing the last Google session is a genuine confidentiality problem.
googleProvider.setCustomParameters({ prompt: 'select_account' });

/* ── Firestore ──────────────────────────────────────────────────────────────
   Persistent cache with multi-tab sync. This is what makes the app usable on
   hospital wifi: reads are served locally and writes queue until reconnection,
   so the UI never blocks on a flaky network.
   ────────────────────────────────────────────────────────────────────────── */
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  // Some hospital proxies break gRPC streaming; this makes listeners fall back
  // to long-polling automatically instead of hanging forever.
  experimentalAutoDetectLongPolling: true,
});

export const storage = getStorage(app);

// Must match setGlobalOptions({ region }) in functions/index.js.
export const functions = getFunctions(app, 'europe-west1');

/* ── Emulators ───────────────────────────────────────────────────────────── */
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  const { connectAuthEmulator } = await import('firebase/auth');
  const { connectFirestoreEmulator } = await import('firebase/firestore');
  const { connectStorageEmulator } = await import('firebase/storage');
  const { connectFunctionsEmulator } = await import('firebase/functions');

  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}
